/**
 * 브랜드 트렌드 랭킹의 **손잡이 값들** — 얼마나 볼지, 몇 개씩 보일지, 어디까지 셀지.
 *
 * ⚠️ 기간 옵션에 **플랜 잠금**이 걸려 있다 — 판정은 `hooks/use-plan-period-limit` 하나이고,
 * 상한을 넘는 저장값은 `DEFAULT_ANALYSIS_PERIOD_DAYS` 로 되돌린다.
 */

import { PERIOD_PRESET_DAYS } from 'src/constants/plan-period';

/**
 * 브랜드 랭킹·게시물 조회 기간 옵션 (일).
 * 🔑 **칸 목록은 `constants/plan-period` 하나**다 — 화면마다 다시 적으면 어긋난다.
 */
export const ANALYSIS_PERIOD_DAY_OPTIONS: readonly number[] = PERIOD_PRESET_DAYS;

/** 잠금 옵션 선택 시 되돌아갈 기본 기간 (일) */
export const DEFAULT_ANALYSIS_PERIOD_DAYS = 30;

/** 기간 Select에서 달력(직접 선택)을 여는 특수 값 */
export const CUSTOM_PERIOD_OPTION_VALUE = '__custom__';

/** 직접 선택으로 거슬러 올라갈 수 있는 최대 기간 (일) — 프리셋 최대치와 동일 */
export const CUSTOM_PERIOD_MAX_DAYS = 180;

/** 브랜드 랭킹 정렬: 언급량(문서수) | 도달(metricScoreSort 합) */
export const BRAND_RANKING_ORDER = Object.freeze({
  MENTIONS: 'mentions',
  REACH: 'reach',
});

export type BrandRankingOrder =
  (typeof BRAND_RANKING_ORDER)[keyof typeof BRAND_RANKING_ORDER];

/** 아무것도 안 고른 상태의 채널·국가 — 「전체」가 아니라 **이 다섯·이 하나**다 */
export const DEFAULT_SOURCE_IDS = Object.freeze([
  'threads',
  'x',
  'youtube',
  'instagram',
  'tiktok',
]);
/**
 * ⚠️ **대문자로 적는다.** 국가 목록(`languageOptions`)이 `normalizeMarketCode` 로
 * 정규화된 코드를 항목 값으로 쓰고, MUI 는 선택 여부를 `value.indexOf(항목값)` 로
 * — 즉 **글자 그대로** — 판정한다. 여기가 `'kr'` 이면 첫 화면의 「대한민국」이
 * 골라진 것으로 안 보여 한 번 눌러도 해제가 아니라 **추가**가 된다.
 */
export const DEFAULT_MARKET_CODES = Object.freeze(['KR']);

/** 브랜드 키워드 API에서 가져올 최대 개수 */
export const BRAND_KEYWORD_FETCH_LIMIT = 100;
/** 브랜드 키워드 카드 한 페이지당 표시 개수 */
export const BRAND_KEYWORD_PAGE_SIZE = 10;

/** 분포(해시태그/성분/피부고민) 페이지당 표시 개수 */
export const DISTRIBUTION_PAGE_SIZE = 10;

/**
 * 페이지네이션을 위해 분석 단계에서 최대 몇 개까지 계산할지.
 *
 * ⚠️ 페이지 크기(`DISTRIBUTION_PAGE_SIZE`)와 **다른 값이다.** 이쪽은 「몇 개를 계산해
 * 둘까」이고 그쪽은 「한 번에 몇 개를 보여 줄까」다 — 이 값이 작으면 뒷장이 비어 버린다.
 */
export const MAX_DISTRIBUTION_ITEMS = 100;

/** 셀링·페인 포인트 분석: 인기순 상위 N개 searchPost 사용 */
export const SENTIMENT_ANALYSIS_POST_LIMIT = 100;

/** 감성 분석에 넘기는 글 하나의 최대 길이 */
export const SEARCH_POST_TEXT_MAX_CHARS = 1000;
